import { parseURLParams } from "@/utils/url";
import {
  BRANDS,
  SECONDARY_BRANDS,
  SEGMENTS,
} from "@/constant/constantsThrougoutProject";
import { isOptionSelection, type FilterSelection } from "@interfaces/filters/domain";
import {
  SELECTIONS_CAPS,
  type FilterRefInput,
  type SelectionsValidateRequest,
} from "@/interfaces/selections/selections.types";

export interface SelectionsRefOrigin {
  /** Which selection field each brand-set ULID came from, so results route back correctly. */
  brandSetOrigin: Record<string, "brands" | "secondaryBrands">;
}

export interface TruncatedKinds {
  brands: boolean;
  brandSets: boolean;
  filters: boolean;
  segments: boolean;
}

export interface BuildValidateResult {
  request: SelectionsValidateRequest;
  origin: SelectionsRefOrigin;
  truncated: TruncatedKinds;
}

const asArray = (v: unknown): (number | string)[] =>
  Array.isArray(v) ? (v as (number | string)[]) : [];

export function buildValidateRequest(
  searchParams: URLSearchParams,
): BuildValidateResult {
  const parsed = parseURLParams(searchParams);
  const brandSetOrigin: Record<string, "brands" | "secondaryBrands"> = {};
  const truncated: TruncatedKinds = {
    brands: false,
    brandSets: false,
    filters: false,
    segments: false,
  };

  const brands: number[] = [];
  const brandSets: string[] = [];

  const collect = (key: "brands" | "secondaryBrands", raw: (number | string)[]) => {
    for (const v of raw) {
      if (typeof v === "number") {
        if (!brands.includes(v)) brands.push(v);
      } else if (typeof v === "string" && v !== "") {
        if (!(v in brandSetOrigin)) {
          brandSets.push(v);
          brandSetOrigin[v] = key;
        }
      }
    }
  };
  collect("brands", asArray(parsed[BRANDS]));
  collect("secondaryBrands", asArray(parsed[SECONDARY_BRANDS]));

  // brands + brandSets share a single 200 budget; brands take priority in the URL order.
  const total = brands.length + brandSets.length;
  let cappedBrands = brands;
  let cappedBrandSets = brandSets;
  if (total > SELECTIONS_CAPS.brandsAndBrandSets) {
    cappedBrands = brands.slice(0, SELECTIONS_CAPS.brandsAndBrandSets);
    const remaining = SELECTIONS_CAPS.brandsAndBrandSets - cappedBrands.length;
    cappedBrandSets = brandSets.slice(0, Math.max(0, remaining));
    // Flag each kind by whether its own list actually shrank -- the combined
    // budget can overflow while only brandSets (dropped first) lose anything.
    truncated.brands = cappedBrands.length < brands.length;
    truncated.brandSets = cappedBrandSets.length < brandSets.length;
  }

  const segmentsRaw = (parsed[SEGMENTS] as string[] | undefined) ?? [];
  const segments = [...new Set(segmentsRaw.filter((s) => s !== ""))];
  let cappedSegments = segments;
  if (segments.length > SELECTIONS_CAPS.segments) {
    truncated.segments = true;
    cappedSegments = segments.slice(0, SELECTIONS_CAPS.segments);
  }

  const filterSelections = (parsed.filters as FilterSelection[] | undefined) ?? [];
  const filters: FilterRefInput[] = filterSelections.map((f) =>
    isOptionSelection(f) ? { code: f.code, options: f.options } : { code: f.code },
  );
  let cappedFilters = filters;
  if (filters.length > SELECTIONS_CAPS.filters) {
    truncated.filters = true;
    cappedFilters = filters.slice(0, SELECTIONS_CAPS.filters);
  }

  const request: SelectionsValidateRequest = {};
  if (cappedBrands.length) request.brands = cappedBrands;
  if (cappedBrandSets.length) request.brandSets = cappedBrandSets;
  if (cappedFilters.length) request.filters = cappedFilters;
  if (cappedSegments.length) request.segments = cappedSegments;

  return { request, origin: { brandSetOrigin }, truncated };
}
